import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import { selectUserInformations } from "../../features/authSlice";
import { API } from "../../shared/api/Api";
import { showToast } from "../../utils/utils";
import { formatPhoneNumberForDisplay } from "../../utils/phoneUtils";
import StudentHistory from "../../components/Student/StudentHistory";
import Loader from "../../components/Live/Loader";
import Pagination from "../../components/reusable/Pagination";
import {
  FiSearch,
  FiUser,
  FiArrowLeft,
  FiPhone,
  FiHash,
  FiCalendar,
  FiX,
  FiChevronRight,
  FiActivity,
} from "react-icons/fi";

const DEBOUNCE_DELAY = 500;

const StudentHistorySearchPage = () => {
  const user = useSelector(selectUserInformations);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentWithHistory, setStudentWithHistory] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) {
        setCurrentPage(1);
        setHasSearched(true);
      }
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [search]);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    if (!debouncedSearch.trim()) {
      setStudents([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get(API.Students.app_students_all, {
        params: {
          page: currentPage,
          search: debouncedSearch,
          limit: 10,
        },
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setStudents(data.data.items || []);
        setTotalPages(data.data.last_page || 1);
      } else {
        showToast("Error", data.message || "Failed to search students", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to search students", "error");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage, user.token]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Fetch full student data with history
  const fetchStudentWithHistory = useCallback(async (studentId) => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API.Students.app_students_get}/${studentId}`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      if (data.status === 200 && data.errorCode === 0) {
        setStudentWithHistory(data.data);
      } else {
        showToast("Error", data.message || "Failed to fetch student history", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to fetch student history", "error");
    } finally {
      setHistoryLoading(false);
    }
  }, [user.token]);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setStudentWithHistory(null);
    fetchStudentWithHistory(student.id);
  };

  const handleBackToSearch = () => {
    setSelectedStudent(null);
    setStudentWithHistory(null);
  };

  const clearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setStudents([]);
    setHasSearched(false);
    setSelectedStudent(null);
    setStudentWithHistory(null);
  };

  return (
    <div className="min-h-screen p-2 sl:pt-0 sm:p-2 md:p-4 sl:p-6">
      <div className="w-full max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard/students"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
            >
              <FiArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <FiActivity className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                  Student History Search
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Search for a student to view their complete academic history
                </p>
              </div>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!selectedStudent ? (
            <motion.div
              key="search"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6"
            >
              {/* Search Input */}
              <div className="relative max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Student ID, First Name, Last Name, Mobile..."
                    className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-all"
                  />
                  {search && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <FiX className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                  Start typing to search for students
                </p>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="flex justify-center py-12">
                  <Loader />
                </div>
              )}

              {/* Search Results */}
              {!loading && students.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Search Results ({students.length} students found)
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {students.map((student, index) => (
                      <motion.div
                        key={student.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => handleSelectStudent(student)}
                        className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-2 border-transparent hover:border-blue-500 group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                              <FiUser className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {student.first_name} {student.last_name}
                              </h4>
                              <div className="flex items-center gap-4 mt-1">
                                <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <FiHash className="w-3 h-3" />
                                  ID: {student.id}
                                </span>
                                {student.mobile && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    <FiPhone className="w-3 h-3" />
                                    {formatPhoneNumberForDisplay(student.mobile)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <FiChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
                        </div>
                        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {student.country || 'N/A'}, {student.city || 'N/A'}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            student.present
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                          }`}>
                            {student.present ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-6">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* No Results */}
              {!loading && hasSearched && debouncedSearch && students.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiSearch className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No students found
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Try searching with different keywords
                  </p>
                </div>
              )}

              {/* Initial State */}
              {!loading && !hasSearched && (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiUser className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Search for a Student
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Enter a student's ID, name, or mobile number to view their complete academic history, 
                    including courses, payments, exams, and scores.
                  </p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Selected Student Header */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleBackToSearch}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-700"
                    >
                      <FiArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                      <FiUser className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        {selectedStudent.first_name} {selectedStudent.last_name}
                      </h2>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiHash className="w-4 h-4" />
                          Student ID: {selectedStudent.id}
                        </span>
                        {selectedStudent.mobile && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiPhone className="w-4 h-4" />
                            {formatPhoneNumberForDisplay(selectedStudent.mobile)}
                          </span>
                        )}
                        {selectedStudent.birth_date && (
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <FiCalendar className="w-4 h-4" />
                            {selectedStudent.birth_date}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/dashboard/students/${selectedStudent.id}`}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    View Full Profile
                  </Link>
                </div>
              </div>

              {/* Student History Component */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                <StudentHistory studentData={studentWithHistory} loading={historyLoading} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default StudentHistorySearchPage;
